import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker

interface PdfPreviewProps {
  src: string
  className?: string
  mini?: boolean
  // Optional pre-rendered page-1 image shown immediately so first paint isn't a
  // spinner. The lazy renderer still runs in the background; once page 1 is
  // ready the real render replaces this seamlessly.
  thumbnailUrl?: string
}

// Lazy PDF renderer. Opens the document on `src` change but only rasterises the page(s)
// it actually needs to display, instead of pre-rendering every page upfront. Big perf
// win for multi-page PDFs — initial paint was blocking the main thread for N×canvas
// renders. Now: 1 render per page navigation, plus a small prefetch.
//
// Mini mode keeps its original behaviour (renders first 1-3 pages for the stacked
// preview); full mode lazy-renders.

export const PdfPreview = ({
  src,
  className = '',
  mini = false,
  thumbnailUrl,
}: PdfPreviewProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [pageImages, setPageImages] = useState<Map<number, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Hold the open document across page renders so we don't re-open on each navigation.
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)

  // Open the PDF document on src change — count pages, store the doc handle for lazy
  // page rendering below. Doesn't rasterise anything yet.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setPageImages(new Map())
    setCurrentPage(1)
    pdfDocRef.current = null

    pdfjsLib
      .getDocument(src)
      .promise.then((pdf) => {
        if (cancelled) return
        pdfDocRef.current = pdf
        setNumPages(pdf.numPages)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error loading PDF:', err)
        setError('Failed to load PDF')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [src])

  // Lazily render the pages we actually need: the current page, plus its immediate
  // neighbours as a tiny prefetch. Previously-rendered pages stay cached in the Map.
  // Mini mode renders pages 1..3 once for the stacked preview.
  useEffect(() => {
    const pdf = pdfDocRef.current
    if (!pdf || numPages === 0) return

    const targets = mini
      ? Array.from({ length: Math.min(3, numPages) }, (_, i) => i + 1)
      : [currentPage - 1, currentPage, currentPage + 1].filter((p) => p >= 1 && p <= numPages)

    let cancelled = false
    void Promise.all(
      targets.map(async (pageNum) => {
        if (pageImages.has(pageNum)) return
        const page = await pdf.getPage(pageNum)
        // Scale 1.5 keeps text crisp at typical card sizes while halving the canvas
        // work vs scale=2. Bump if the page looks blurry at the largest layout we use.
        const viewport = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        await page.render({ canvasContext: ctx, viewport, canvas }).promise
        if (cancelled) return
        setPageImages((prev) => {
          if (prev.has(pageNum)) return prev
          const next = new Map(prev)
          next.set(pageNum, canvas.toDataURL())
          return next
        })
      }),
    )
    return () => {
      cancelled = true
    }
  }, [currentPage, numPages, mini, pageImages])

  const goToPrevPage = () => {
    if (numPages === 0) return
    setCurrentPage((p) => (p > 1 ? p - 1 : numPages))
  }

  const goToNextPage = () => {
    if (numPages === 0) return
    setCurrentPage((p) => (p < numPages ? p + 1 : 1))
  }

  if (loading) {
    // If we have a pre-rendered page-1 thumbnail, show it instead of a spinner —
    // first paint feels instant. The lazy renderer is still warming up underneath.
    if (thumbnailUrl) {
      return (
        <div className={`relative overflow-hidden bg-gray-900 ${className}`}>
          <img
            src={thumbnailUrl}
            alt="PDF preview"
            draggable={false}
            className="h-full w-full object-contain"
          />
        </div>
      )
    }
    return (
      <div className={`relative overflow-hidden rounded-lg bg-gray-100 ${className}`}>
        <div className="flex h-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`relative overflow-hidden rounded-lg bg-gray-100 ${className}`}>
        <div className="flex h-full items-center justify-center text-gray-500">
          <span>Failed to load PDF</span>
        </div>
      </div>
    )
  }

  // Mini mode - show up to 3 pages with stacking effect
  if (mini) {
    const images = [1, 2, 3]
      .filter((n) => n <= numPages)
      .map((n) => pageImages.get(n))
      .filter((img): img is string => !!img)
    return (
      <div className={`relative aspect-square h-full ${className}`}>
        {images.map((pageImage, index) => (
          <div
            key={index}
            className="absolute inset-0 h-full w-full"
            style={{
              transform: `translate(${index * 4}px, ${index * 4}px)`,
              opacity: 1 - index * 0.3,
              zIndex: 10 - index,
            }}>
            <img
              src={pageImage}
              alt={`PDF page ${index + 1}`}
              draggable={false}
              className="h-full w-full rounded-md object-cover"
            />
          </div>
        ))}
      </div>
    )
  }

  const currentImage = pageImages.get(currentPage)

  return (
    <div className={`group relative overflow-hidden bg-gray-900 ${className}`}>
      {/* Document header - only visible on hover */}
      <div className="absolute left-3 top-2 z-20 opacity-0 transition-opacity group-hover:opacity-100">
        <span className="rounded bg-black/50 px-2 py-1 text-sm font-medium text-white">
          Document • {numPages} page{numPages !== 1 ? 's' : ''}
        </span>
      </div>

      {/* PDF Page Display */}
      <div className="relative flex h-full w-full items-center justify-center">
        {currentImage ? (
          <img
            src={currentImage}
            alt={`Page ${currentPage}`}
            draggable={false}
            className="max-h-full max-w-full object-contain"
          />
        ) : thumbnailUrl && currentPage === 1 ? (
          // Bridge the gap between the document opening and page 1 finishing its
          // rasterisation — show the pre-rendered thumbnail instead of a spinner.
          <img
            src={thumbnailUrl}
            alt="PDF preview"
            draggable={false}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-white/60" />
        )}
      </div>

      {/* Navigation arrows */}
      {numPages > 1 && (
        <>
          <button
            onClick={goToPrevPage}
            className="absolute left-2 top-1/2 z-20 -translate-y-1/2 transform rounded-full bg-black/50 p-2 text-white opacity-0 transition-all hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-30 group-hover:opacity-100">
            <ChevronLeft size={20} />
          </button>

          <button
            onClick={goToNextPage}
            className="absolute right-2 top-1/2 z-20 -translate-y-1/2 transform rounded-full bg-black/50 p-2 text-white opacity-0 transition-all hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-30 group-hover:opacity-100">
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Page indicator - only visible on hover */}
      {numPages > 1 && (
        <div className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 transform opacity-0 transition-opacity group-hover:opacity-100">
          <div className="rounded-full bg-black/70 px-3 py-1 text-sm text-white">
            {currentPage} / {numPages}
          </div>
        </div>
      )}

      {/* Page dots indicator */}
      {numPages > 1 && numPages <= 10 && (
        <div className="absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 transform space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
          {Array.from({ length: numPages }, (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index + 1)}
              className={`h-2 w-2 rounded-full transition-all ${
                currentPage === index + 1 ? 'bg-white' : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
